import type { ComponentProps, ForwardedRef, JSX } from 'react';

import { Image, ThemeProvider } from '@change/ds-core-ui/next';

import { forwardRef } from '@change-corgi/core/react/core';
import type { QrCodeType } from '@change-corgi/middle-end-gql-schema';

import { usePetitionQRCode } from 'src/app/shared/hooks/qrcode';

// An SVG with a white background which scales to 100% width and height.
// Using a transparent, or white, 1x1 pixel PNG did not work in Firefox; the alt text was shown.
const WHITE_SVG =
	"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100%25' height='100%25' fill='white'/%3E%3C/svg%3E";

/**
 * Renders a petition QR code as an image.
 * Displays a white SVG placeholder of the requested size while the QR code image is loading.
 *
 * @param {string} slugOrId - The petition's slug or ID.
 * @param {number} size - Display size (in pixels) for the QR code image. Used to reserve space and support high-DPI scaling.
 * @param {QrCodeType} type - The type of QR code to render.
 * @param {...any} rest - Additional props to forward to the Image component.
 * @param {ForwardedRef<HTMLImageElement>} ref - Ref passed to the image element.
 * @returns {JSX.Element} The themed image component displaying the QR code.
 */
function PetitionQRCodeInner(
	{
		slugOrId,
		size,
		type,
		...rest
	}: {
		slugOrId: string;
		size: number;
		type: QrCodeType;
	} & ComponentProps<typeof Image>,
	ref: ForwardedRef<HTMLImageElement>,
): JSX.Element {
	const { data } = usePetitionQRCode({
		slugOrId,
		size,
		type,
		adjustForPixelRatio: true,
	});

	return (
		<ThemeProvider>
			<Image
				src={data?.imageBase64Uri || WHITE_SVG}
				width={size}
				height={size}
				alt="Petition QR Code"
				aspectRatio="1:1"
				style={{ minWidth: `${size}px`, minHeight: `${size}px` }}
				{...rest}
				ref={ref}
			/>
		</ThemeProvider>
	);
}

export const PetitionQRCode = forwardRef(PetitionQRCodeInner);
